import { NavigationGuardNext, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import store from '../store';

const hasValueBundleItemsInCart = (valueCoupon: string) => !!store.getters.getCartItems?.some((i: any) => i.bundleIdentifier && i.bundleIdentifier.startsWith(valueCoupon));

const isBundleFlowRoute = (route: RouteLocationNormalized) => route.meta?.bundleFlow === true;

export const discardAbandonedBundleEdit = (to: RouteLocationNormalized) => {
    if (!isBundleFlowRoute(to) && store.getters.getEditingIdentifier) {
        store.dispatch('resetBundle');
    }
};

const ensureValueBundleLoaded = async (): Promise<string | undefined> => {
    if (!store.getters.getValueBundle) {
        await store.dispatch('fetchBundles');
    }
    return store.getters.getValueBundle?.couponNumber;
};

const wasRedirectedWithinBundleFlow = (to: RouteLocationNormalized) => !!to.redirectedFrom && isBundleFlowRoute(to.redirectedFrom as RouteLocationNormalized);

const hasAnsweredResumePrompt = (to: RouteLocationNormalized, from: RouteLocationNormalized) => isBundleFlowRoute(from) || wasRedirectedWithinBundleFlow(to) || to.query.resume === '1';

const valueBundleUpdateGuard = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const valueCoupon = await ensureValueBundleLoaded();
    if (valueCoupon && parseInt(to.params.couponNumber as string) === parseInt(valueCoupon) && !hasAnsweredResumePrompt(to, from)) {
        const isEditing = !!store.getters.getEditingIdentifier;
        const hasSomethingToResume = hasValueBundleItemsInCart(valueCoupon) || store.getters.hasStagedBundleFor(valueCoupon);
        if (hasSomethingToResume && !isEditing) {
            next('/pizza-pals');
            return;
        }
    }
    next();
};

const accountRoutes: Array<RouteRecordRaw> = [
    {
        path: '/login',
        name: 'Login',
        meta: {
            title: 'Login',
            description: '',
        },
        component: () => import('@/views/account/loginPage.vue'),
    },
    {
        path: '/signup',
        name: 'Sign up',
        meta: {
            title: 'Sign up',
            description: '',
        },
        component: () => import('@/views/account/signUpPage.vue'),
    },
    {
        path: '/account/deals',
        redirect: '/deals',
    },
    {
        path: '/account/offers',
        redirect: '/deals',
    },
    {
        path: '/offers',
        redirect: '/deals',
    },
    {
        path: '/account/rewards',
        redirect: '/rewards',
    },
    {
        path: '/deals',
        name: 'Deals',
        meta: {
            title: 'Deals',
            description: '',
        },
        component: () => import('@/views/account/dealsPage.vue'),
    },
    {
        path: '/pizza-pals',
        name: 'Pizza Pals',
        meta: {
            awaitLocation: true,
            bundleFlow: true,
        },
        component: () => import('@/views/bundle/pizzaPalsPage.vue'),
        beforeEnter: async (to, from, next) => {
            const couponNumber = await ensureValueBundleLoaded();
            if (!couponNumber) {
                next('/menu');
            } else if (hasValueBundleItemsInCart(couponNumber) || store.getters.hasStagedBundleFor(couponNumber)) {
                next(); // an existing bundle or an unfinished build -> show the prompt
            } else if (wasRedirectedWithinBundleFlow(to)) {
                next(); // the builder sent us here; stop rather than send it back
            } else {
                next(`/pizza-pals/${couponNumber}/1`); // nothing to resume -> start a fresh build
            }
        },
    },
    {
        path: '/deals/:couponNumber',
        name: 'Deal Builder',
        meta: {
            title: 'Deal Builder',
            description: '',
            hideTabNavigation: true,
            awaitLocation: true,
            bundleFlow: true,
        },
        alias: '/pizza-pals/:couponNumber',
        component: () => import('@/views/bundle/bundlePage.vue'),
        beforeEnter: valueBundleUpdateGuard,
    },
    {
        path: '/deals/:couponNumber/:groupNumber',
        name: 'Deal Builder Group',
        meta: {
            title: 'Deal Builder',
            description: '',
            hideTabNavigation: true,
            awaitLocation: true,
            bundleFlow: true,
        },
        alias: '/pizza-pals/:couponNumber/:groupNumber',
        component: () => import('@/views/bundle/groupPage.vue'),
        beforeEnter: valueBundleUpdateGuard,
    },
    {
        path: '/deals/:couponNumber/:groupNumber/:itemNumber',
        name: 'Deal Builder Item',
        meta: {
            title: 'Deal Builder',
            description: '',
            hideTabNavigation: true,
            awaitLocation: true,
            bundleFlow: true,
        },
        alias: '/pizza-pals/:couponNumber/:groupNumber/:itemNumber',
        component: () => import('@/views/bundle/itemPage.vue'),
        beforeEnter: valueBundleUpdateGuard,
    },
    {
        path: '/rewards',
        meta: {
            seo: {
                title: 'Donatos Rewards',
                description: 'Points, Perks and Free Pizza! Start earning rewards today. Sign up or log in.',
                keywords: ['Donatos rewards', 'Donatos loyalty program', 'earn rewards Donatos', 'Donatos perks', 'free pizza rewards'],
            },
            requiresAuth: true,
            authRedirect: '/about-rewards',
        },
        component: () => import('@/views/account/rewardsAccountPage.vue'),
    },
    {
        path: '/rewards/history',
        name: 'Rewards History',
        meta: {
            title: 'Rewards History',
            requiresAuth: true,
            authRedirect: '/about-rewards',
        },
        component: () => import('@/views/account/rewardsHistoryPage.vue'),
    },
    {
        path: '/history',
        name: 'history',
        meta: {
            title: 'Account History',
        },
        props: true,
        component: () => import('@/views/account/historyPage.vue'),
    },
    {
        path: '/account',
        name: 'My Account',
        meta: {
            title: 'My Account',
            description: '',
            hideTabNavigation: true,
            backBtn: true,
            requiresAuth: true,
            authRedirect: '/login?redirect=/account',
        },
        component: () => import('@/views/account/accountPage.vue'),
    },
];

export default accountRoutes;
